import { compileFetchedEntity } from '../../shared/compile-fetched-entity';
import { fromBaseRecord } from '../compile/from-base-context';
import { readItemUuidSlot } from '../compile/inventory-raw-readers';
import type { InventorySharedCatalog } from '../compile/inventory-shared-catalog';

export type DefinitionRow = Record<string, unknown>;
export interface DefinitionReaders {
    item(row: unknown, key: string): unknown;
    layer(row: unknown, key: string): unknown;
}
export const definitionReaders: DefinitionReaders = {
    item: (row, key) => window.ftItemsCol(row, key),
    layer: (row, key) => window.ftLayerValue(row, key),
};

/** Raw SELECT packages join the existing page owner; this module owns no compiler or cache. */
export async function compileDefinitionInputs(value: unknown, ids: readonly string[]): Promise<Map<string, DefinitionRow | null>> {
    if (!fromBaseRecord(value) || value.shape_v !== 2 || !fromBaseRecord(value.definitions)
        || !fromBaseRecord(value.inputs) || !fromBaseRecord(value.catalog) || typeof value.engine !== 'string')
        throw new Error('Definition inputs unavailable. Please retry.');
    const catalog = value.catalog as unknown as InventorySharedCatalog;
    const revisions = value.inputs.dependency_revs;
    const result = new Map<string, DefinitionRow | null>();
    for (const selected of ids) {
        const id = selected.toUpperCase();
        if (result.has(id)) continue;
        const definition = value.definitions[id];
        if (!fromBaseRecord(definition)) { result.set(id, null); continue; }
        const revision = fromBaseRecord(revisions) ? revisions[id] : undefined;
        if (!Number.isInteger(revision) || Number(revision) < 0) throw new Error('Definition revision unavailable. Please retry.');
        const host = await compileFetchedEntity({ items: definition.items, _entity_base: {
            entity: definitionReaders.item(definition, 'item_type') === 'creature' ? 'creature' : 'definition', entity_id: id, base_rev: revision, engine: value.engine,
            base: { ...value.inputs, ...definition, entity_id: id, base_rev: revision }, cache: null,
        } }, catalog);
        if (String(definitionReaders.item(host, 'item_id') ?? '').toUpperCase() !== id || host._entity_refusal)
            throw new Error('Definition compilation incomplete. Please retry.');
        result.set(id, host);
    }
    return result;
}

export const AMBIENCE_SLOTS = ['encounter_amb', 'encounter_amb_night', 'encounter_amb_weather'] as const;
export function locationTypeAmbience(type: DefinitionRow, audioRows: readonly DefinitionRow[], read: DefinitionReaders = definitionReaders) {
    return AMBIENCE_SLOTS.map(prefix => {
        const id = readItemUuidSlot(read.layer(type, `default_${prefix}_id`));
        const audio = audioRows.find(row => String(read.item({ items: row }, 'audio_id') ?? '').toUpperCase() === id.toUpperCase());
        return { prefix, id, cat: audio ? read.item({ items: audio }, 'audio_category') ?? null : null,
            sub: audio ? read.item({ items: audio }, 'audio_subcategory') ?? null : null };
    });
}
